import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { expenseService } from '../services/expenseService';
import { propertyService } from '../services/propertyService';
import { ticketService } from '../services/ticketService';
import Sidebar from '../components/Sidebar';

const ExpensesPage = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showExpenseDetail, setShowExpenseDetail] = useState(false);
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [newExpense, setNewExpense] = useState({
    amount: '',
    description: '',
    category: 'maintenance',
    propertyId: '',
    ticketId: '', // Optional - can be empty
    date: new Date().toISOString().split('T')[0],
    receiptUrl: '',
    notes: '',
    expenseType: 'manual', // manual, provider_upload, auto_generated
    status: 'pending' // pending, approved, rejected
  });

  const categories = [
    'maintenance',
    'repair',
    'utilities',
    'insurance',
    'taxes',
    'cleaning',
    'landscaping',
    'security',
    'furniture',
    'appliances',
    'other'
  ];



  useEffect(() => {
    if (currentUser && userData) {
      const fetchData = async () => {
        try {
          // Fetch properties for the owner
          const ownerProperties = await propertyService.getPropertiesByOwner(currentUser.uid);
          setProperties(ownerProperties || []);

          // Fetch tickets for the owner (for optional linking)
          const ownerTickets = await ticketService.getTicketsByUser(currentUser.uid, 'owner');
          setTickets(ownerTickets || []);

          // Subscribe to expenses by owner
          const unsubscribe = expenseService.subscribeToExpensesByOwner(currentUser.uid, (expenses) => {
            setExpenses(expenses);
            setLoading(false);
          });

          return unsubscribe;
        } catch (error) {
          console.error('Error fetching data:', error);
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [currentUser, userData]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      // Validate required fields
      if (!newExpense.amount || !newExpense.description || !newExpense.propertyId) {
        alert('Please fill in all required fields');
        return;
      }

      await expenseService.createExpense({
        ...newExpense,
        ownerId: currentUser.uid,
        amount: parseFloat(newExpense.amount),
        date: new Date(newExpense.date),
        createdBy: currentUser.uid,
        createdByType: 'owner'
      });

      setShowAddExpense(false);
      setNewExpense({
        amount: '',
        description: '',
        category: 'maintenance',
        propertyId: '',
        ticketId: '',
        date: new Date().toISOString().split('T')[0],
        receiptUrl: '',
        notes: '',
        expenseType: 'manual',
        status: 'pending'
      });
      alert('Expense added successfully!');
    } catch (error) {
      console.error('Error adding expense:', error);
      alert('Error adding expense: ' + error.message);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In a real implementation, you would upload to Firebase Storage
      // For now, we'll just store the file name
      setNewExpense(prev => ({
        ...prev,
        receiptUrl: file.name
      }));
    }
  };

  const getFilteredExpenses = () => {
    let filtered = [...expenses];

    // Filter by property
    if (filterProperty !== 'all') {
      filtered = filtered.filter(expense => expense.propertyId === filterProperty);
    }

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(expense => expense.category === filterCategory);
    }

    // Filter by date range
    if (filterDateRange !== 'all') {
      const now = new Date();
      
      switch (filterDateRange) {
        case 'today':
          filtered = filtered.filter(expense => {
            const expDate = expense.date?.toDate?.() || new Date(expense.date);
            return expDate.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(expense => {
            const expDate = expense.date?.toDate?.() || new Date(expense.date);
            return expDate >= weekAgo;
          });
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(expense => {
            const expDate = expense.date?.toDate?.() || new Date(expense.date);
            return expDate >= monthAgo;
          });
          break;
        default:
          break;
      }
    }

    return filtered;
  };

  const getCategoryColor = (category) => {
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'maintenance':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'repair':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
          </svg>
        );
      case 'utilities':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'insurance':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      case 'taxes':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'approved':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'rejected':
        return 'bg-slate-100 text-slate-500 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name || property.address : 'Unknown Property';
  };

  const getTicketTitle = (ticketId) => {
    if (!ticketId) return 'No Ticket';
    const ticket = tickets.find(t => t.id === ticketId);
    return ticket ? ticket.title : 'Unknown Ticket';
  };

  const totalExpenses = getFilteredExpenses().reduce((sum, expense) => sum + (expense.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-72">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
            <p className="mt-4 text-sm font-medium text-slate-600">Loading expenses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-slate-50">
        <div className="flex">
          <Sidebar />
          <div className="flex-1 ml-72 -ml-8">
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
              <div className="flex items-center justify-between px-8 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-slate-800">{t('dashboard.owner.pages.expenses.title')}</h1>
                    <p className="text-slate-500 text-sm">{t('dashboard.owner.pages.expenses.subtitle')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                    {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                  <button
                    onClick={() => setShowAddExpense(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.75} />
                    Add Expense
                  </button>
                </div>
              </div>
            </header>

            <div className="p-8">
              <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                  <select
                    value={filterProperty}
                    onChange={(e) => setFilterProperty(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  >
                    <option value="all">All Properties</option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>{property.name || property.address}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category.charAt(0).toUpperCase() + category.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date Range</label>
                  <select
                    value={filterDateRange}
                    onChange={(e) => setFilterDateRange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 w-full">
                    <p className="text-sm text-slate-600 font-medium">Total</p>
                    <p className="text-xl font-semibold text-slate-800">${totalExpenses.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

              <div className="bg-white rounded-lg border border-slate-200">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 mb-4">Recent Expenses</h2>
                  {getFilteredExpenses().length === 0 ? (
                    <div className="text-center py-10">
                      <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.25} />
                      <p className="text-slate-500">No expenses found</p>
                      <button
                        onClick={() => setShowAddExpense(true)}
                        className="mt-2 text-slate-600 hover:text-slate-800 font-medium text-sm"
                      >
                        Add your first expense
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getFilteredExpenses().map(expense => (
                        <div
                          key={expense.id}
                          className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedExpense(expense);
                            setShowExpenseDetail(true);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className={`p-2 flex-shrink-0 rounded-md border ${getCategoryColor(expense.category)}`}>
                                {getCategoryIcon(expense.category)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-slate-800 truncate">{expense.description}</h3>
                                <p className="text-sm text-slate-500 truncate">
                                  {getPropertyName(expense.propertyId)} • {expense.category}
                                </p>
                                {expense.ticketId && (
                                  <p className="text-xs text-slate-500 truncate">
                                    Linked to: {getTicketTitle(expense.ticketId)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-4">
                              <p className="text-lg font-semibold text-slate-800">${expense.amount?.toFixed(2)}</p>
                              <p className="text-sm text-slate-500">
                                {expense.date?.toDate?.()?.toLocaleDateString() || new Date(expense.date).toLocaleDateString()}
                              </p>
                              <span className={`inline-block px-2 py-0.5 text-xs rounded border ${getStatusColor(expense.status)}`}>
                                {expense.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
           </div>
         </div>
       </div>
     </div>

            {showAddExpense && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-semibold text-slate-800">Add New Expense</h2>
              <button
                onClick={() => setShowAddExpense(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={newExpense.date}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    value={newExpense.category}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, category: e.target.value }))}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category.charAt(0).toUpperCase() + category.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property *</label>
                  <select
                    value={newExpense.propertyId}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, propertyId: e.target.value }))}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                  >
                    <option value="">Select Property</option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>{property.name || property.address}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ticket (Optional)</label>
                <select
                  value={newExpense.ticketId}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, ticketId: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                >
                  <option value="">No Ticket</option>
                  {tickets.map(ticket => (
                    <option key={ticket.id} value={ticket.id}>{ticket.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Receipt Upload</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={newExpense.notes}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:border-slate-300"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowAddExpense(false)}
                  className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-700 text-white hover:bg-slate-800 rounded-md text-sm font-medium transition-colors"
                >
                  Add Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showExpenseDetail && selectedExpense && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-semibold text-slate-800">Expense Details</h2>
              <button
                onClick={() => setShowExpenseDetail(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg border ${getCategoryColor(selectedExpense.category)}`}>
                  {getCategoryIcon(selectedExpense.category)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">{selectedExpense.description}</h3>
                  <p className="text-slate-500">{selectedExpense.category}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600">Amount</label>
                  <p className="text-lg font-semibold text-slate-800">${selectedExpense.amount?.toFixed(2)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600">Date</label>
                  <p className="text-slate-800">
                    {selectedExpense.date?.toDate?.()?.toLocaleDateString() || new Date(selectedExpense.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600">Property</label>
                <p className="text-slate-800">{getPropertyName(selectedExpense.propertyId)}</p>
              </div>
              {selectedExpense.ticketId && (
                <div>
                  <label className="block text-sm font-medium text-slate-600">Linked Ticket</label>
                  <p className="text-slate-800">{getTicketTitle(selectedExpense.ticketId)}</p>
                </div>
              )}
              {selectedExpense.notes && (
                <div>
                  <label className="block text-sm font-medium text-slate-600">Notes</label>
                  <p className="text-slate-800">{selectedExpense.notes}</p>
                </div>
              )}
              {selectedExpense.receiptUrl && (
                <div>
                  <label className="block text-sm font-medium text-slate-600">Receipt</label>
                  <p className="text-slate-600 hover:text-slate-800 cursor-pointer">{selectedExpense.receiptUrl}</p>
                </div>
              )}
              <div className="flex justify-end pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowExpenseDetail(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-md text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpensesPage; 